import React, { useState } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { AlertTriangle, Gift, Trash2, X } from "lucide-react";

const STEPS = { CONFIRM: 0, OFFER: 1, FINAL: 2 };

const getAuthConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

export const DeleteAccountModal = ({ open, onOpenChange }) => {
  const [step, setStep] = useState(STEPS.CONFIRM);
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setStep(STEPS.CONFIRM); setConfirmText(""); setLoading(false); };
  const close = () => { reset(); onOpenChange(false); };

  const acceptOffer = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/account/retention-offer`, {}, getAuthConfig());
      toast.success("50% discount applied to your next billing cycle!");
      close();
    } catch {
      toast.error("Failed to apply discount. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const deleteAccount = async () => {
    if (confirmText !== "DELETE") return;
    setLoading(true);
    try {
      await axios.delete(`${API}/account/delete`, {
        ...getAuthConfig(),
        data: { confirmation: "DELETE" },
      });
      toast.success("Account deleted successfully.");
      localStorage.removeItem("token");
      window.location.href = "/";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete account.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md bg-background border-theme" data-testid="delete-account-modal">

        {/* Step 1: Initial Confirmation */}
        {step === STEPS.CONFIRM && (
          <>
            <DialogHeader>
              <div className="mx-auto w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mb-2">
                <AlertTriangle className="w-7 h-7 text-red-400" />
              </div>
              <DialogTitle className="text-center text-foreground text-lg">Delete Your Account?</DialogTitle>
              <DialogDescription className="text-center text-muted-foreground text-sm">
                This will permanently remove your account and all associated data including customers, jobs, invoices, messages, and team members.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 pt-4">
              <Button
                onClick={() => setStep(STEPS.OFFER)}
                variant="destructive"
                className="w-full"
                data-testid="delete-account-continue-btn"
              >
                I want to delete my account
              </Button>
              <Button onClick={close} variant="outline" className="w-full border-theme text-muted-foreground">
                Cancel
              </Button>
            </div>
          </>
        )}

        {/* Step 2: Retention Offer */}
        {step === STEPS.OFFER && (
          <>
            <DialogHeader>
              <div className="mx-auto w-14 h-14 rounded-full bg-[#FFCC00]/10 flex items-center justify-center mb-2">
                <Gift className="w-7 h-7 text-[#FFCC00]" />
              </div>
              <DialogTitle className="text-center text-foreground text-lg">Wait — We'd Hate to See You Go!</DialogTitle>
              <DialogDescription className="text-center text-muted-foreground text-sm">
                Before you leave, we'd like to offer you a special deal.
              </DialogDescription>
            </DialogHeader>
            <div className="my-4 p-4 rounded-xl bg-[#FFCC00]/5 border border-[#FFCC00]/20 text-center">
              <p className="text-2xl font-bold text-[#FFCC00] font-['Manrope']">50% OFF</p>
              <p className="text-muted-foreground text-sm mt-1">Your next billing cycle</p>
            </div>
            <div className="space-y-3">
              <Button
                onClick={acceptOffer}
                disabled={loading}
                className="w-full bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90 font-semibold"
                data-testid="accept-offer-btn"
              >
                {loading ? "Applying..." : "Accept Offer & Stay"}
              </Button>
              <Button
                onClick={() => setStep(STEPS.FINAL)}
                variant="ghost"
                className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
                data-testid="reject-offer-btn"
              >
                No thanks, delete my account
              </Button>
            </div>
          </>
        )}

        {/* Step 3: Final Confirmation + Type DELETE */}
        {step === STEPS.FINAL && (
          <>
            <DialogHeader>
              <div className="mx-auto w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mb-2">
                <Trash2 className="w-7 h-7 text-red-400" />
              </div>
              <DialogTitle className="text-center text-foreground text-lg">Final Step</DialogTitle>
              <DialogDescription className="text-center text-muted-foreground text-sm">
                Type <strong className="text-foreground">DELETE</strong> below to confirm. This action is permanent and cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder='Type "DELETE" to confirm'
                className="input-glass text-center text-lg tracking-widest"
                data-testid="delete-confirm-input"
              />
              <Button
                onClick={deleteAccount}
                disabled={confirmText !== "DELETE" || loading}
                variant="destructive"
                className="w-full"
                data-testid="delete-account-final-btn"
              >
                {loading ? "Deleting..." : "Permanently Delete My Account"}
              </Button>
              <Button onClick={() => setStep(STEPS.OFFER)} variant="ghost" className="w-full text-muted-foreground">
                Go Back
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
